import { Config } from '@langchain/langgraph-sdk';

import { JSONSchema7, JSONSchema7Definition } from 'json-schema';
import { useLayoutEffect } from 'react';
import semver from 'semver';

import { RunSchema } from '@/types/schema';

import { SchemaBundle } from './control/state';
import { JSONValue } from './data/json';
import { Graph } from './data/misc';
import { getConfigFieldNodes, getSchemaProperties } from './json-schema-utils';

const messageTypeTitles = new Set([
  'AIMessage',
  'BaseMessage',
  'ChatMessage',
  'FunctionMessage',
  'HumanMessage',
  'SystemMessage',
  'ToolMessage',
]);

const messageTypes = new Set([
  ...Array.from(messageTypeTitles).map((title) => `#/definitions/${title}`),
  ...Array.from(messageTypeTitles).map((title) => `#/$defs/${title}`),
]);

export const groupBy = <A>(xs: A[], by: (x: A) => string) => {
  return xs.reduce((acc, x) => {
    const key = by(x);
    acc[key] = (acc[key] ?? []).concat([x]);
    return acc;
  }, {} as Record<string, A[]>);
};

export const concatRecordValues = <A>(
  a: Record<string, A[]>,
  b: Record<string, A[]>
) => {
  const allKeys = new Set([...Object.keys(a), ...Object.keys(b)]);
  return [...allKeys].reduce((acc, key) => {
    acc[key] = (a[key] ?? []).concat(b[key] ?? []);
    return acc;
  }, {} as Record<string, A[]>);
};

export const mapRecord = <A, B>(
  a: Record<string, A>,
  f: (x: A) => B
): Record<string, B> =>
  Object.fromEntries(Object.entries(a).map(([key, value]) => [key, f(value)]));

export const isObj = (obj: unknown): obj is Record<keyof any, unknown> =>
  obj != null && typeof obj === 'object';

export const isPlainObj = (obj: unknown): obj is Record<string, unknown> =>
  isObj(obj) && !Array.isArray(obj);

const SATURATION_RANGE: [number, number] = [50, 70];
const LIGHTNESS_RANGE: [number, number] = [40, 90];

export type HSLA = [h: number, s: number, l: number, a: number];

export const numberToColor = (
  number: number,
  light = false,
  alpha = 1
): HSLA => {
  const goldenRatioConjugate = 0.618033988749895;

  // Constants for a linear congruential generator (LCG)
  const a = 1664525;
  const c = 1013904223;
  const m = Math.pow(2, 32);

  // Generate a pseudorandom number using the LCG.
  const pseudorandom = (a * number + c) % m;

  // Compute a hue value using the golden angle.
  const hue = ((pseudorandom * goldenRatioConjugate) % 1) * 360;

  const saturationDiff = SATURATION_RANGE[1] - SATURATION_RANGE[0];
  const lightnessDiff = LIGHTNESS_RANGE[1] - LIGHTNESS_RANGE[0];
  const s = SATURATION_RANGE[0] + (pseudorandom % (saturationDiff + 1));
  const l = light
    ? LIGHTNESS_RANGE[1]
    : LIGHTNESS_RANGE[0] + (pseudorandom % (lightnessDiff + 1));

  return [hue, s, l, alpha];
};

export const hslaToStr = ([h, s, l, a]: HSLA) =>
  `hsla(${h}, ${s}%, ${l}%, ${a})`;

export const numberToColorStr = (number: number, light = false, a = 1) => {
  const [hue, s, l, alpha] = numberToColor(number, light, a);
  return `hsl(${hue}, ${s}%, ${l}%, ${alpha})`;
};

export const hslaWithLightness = (
  [h, s, _, a]: HSLA,
  lightness: number
): HSLA => {
  return [h, s, lightness, a];
};

export const hslaWithMinLightness = (
  [h, s, l, a]: HSLA,
  lightness: number
): HSLA => {
  return [h, s, Math.min(lightness, l), a];
};

export const hslaWithAlpha = ([h, s, l, _]: HSLA, alpha: number): HSLA => {
  return [h, s, l, alpha];
};

export const hashString = (data: string): number => {
  const FNV_prime = 0x01000193;
  const offset_basis = 0x811c9dc5;
  let hash = offset_basis;
  for (let i = 0; i < data.length; i++) {
    hash ^= data.charCodeAt(i);
    hash *= FNV_prime;
    hash >>>= 0;
  }
  return hash;
};

export const truncateString = (str: string, maxLength = 60) => {
  if (str.length <= maxLength) return str;
  return `${str.slice(0, maxLength - 3).trim()}...`;
};

export const pick = <A, K extends keyof A>(
  a: A,
  ks: K[] | undefined
): Pick<A, K> => {
  if (ks == null) return a;
  return ks.reduce((acc, k) => ({ ...acc, [k]: a[k] }), a as Pick<A, K>);
};

export const tryParseJSON = (
  str: string
): { ok: false } | { ok: true; value: JSONValue } => {
  try {
    return { ok: true, value: JSON.parse(str) };
  } catch (e) {
    return { ok: false };
  }
};

export const isEmptyObj = (obj: Record<string, unknown>): boolean =>
  Object.keys(obj).length === 0;

export const takeWhile = <A>(f: (a: A) => boolean, xs: A[]): A[] => {
  const res: A[] = [];
  for (const x of xs) {
    if (f(x)) res.push(x);
    else break;
  }
  return res;
};

export const useEnterSubmit = (disable: boolean, onClick: () => void) => {
  useLayoutEffect(() => {
    function callback(event: KeyboardEvent) {
      if (
        (event.key === 'Enter' || event.key === 'NumpadEnter') &&
        (event.metaKey || event.ctrlKey) &&
        !disable
      ) {
        onClick();
      }
    }
    document.addEventListener('keydown', callback);
    return () => document.removeEventListener('keydown', callback);
  }, [disable, onClick]);
};

/**
 * Parses a checkpoint namespace string into an array of name-id pairs
 * @param checkpointNs - A string containing checkpoint namespaces in the format "name:id|name2:id2"
 * @returns An array of objects, each containing a name and id property
 */
export const parseLangGraphCheckpointNs = (checkpointNs?: string) => {
  if (!checkpointNs) return [];
  return checkpointNs.split('|').map((x) => {
    const [name, id] = x.split(':');
    return { name, id };
  });
};

export const isHostedUrl = (url: string) => {
  try {
    const parsed = new URL(url);
    return parsed.hostname.endsWith('.langgraph.app');
  } catch (e) {
    return false;
  }
};

// List available editable nodes for the
// particular namespace. Only permit those nodes, which are direct
// descendants of the namespace. Also add __start__ nodes if not present
export const getEditableNodes = (
  graph: Graph | undefined,
  namespace: string | undefined
) => {
  const nodes = [
    ...new Set([
      ...Object.keys(graph?.subgraphs ?? {}).flatMap((i) => {
        const ns = i.split('|').join(':');
        return [ns, ns + ':' + '__start__'];
      }),
      ...(graph?.nodes.map((n) => n.id) ?? []),
    ]),
  ];

  const nsPrefix = namespace ? namespace.split('|').join(':') + ':' : '';
  return nodes
    .filter(
      (n) =>
        (!nsPrefix || n.startsWith(nsPrefix)) &&
        !n.slice(nsPrefix.length).includes(':')
    )
    .map((n) => n.slice(nsPrefix.length))
    .sort((a, b) => {
      const aStart = a === '__start__';
      const bStart = b === '__start__';
      if (aStart !== bStart) return aStart ? -1 : 1;

      const aEnd = a === '__end__';
      const bEnd = b === '__end__';
      if (aEnd !== bEnd) return aEnd ? 1 : -1;

      return a.localeCompare(b);
    });
};

export const isConfigSchemaEmpty = (schema: SchemaBundle) => {
  return (
    typeof schema.config !== 'object' ||
    schema.config == null ||
    !('properties' in schema.config) ||
    Object.keys(schema.config.properties ?? {}).length === 0
  );
};

export const getConfigFieldsForNode = ({
  configurable,
  configSchema,
  nodeId,
}: {
  configurable: Config['configurable'];
  configSchema: JSONSchema7Definition | null | undefined;
  nodeId: string;
}) => {
  return Object.fromEntries(
    Object.entries(configurable ?? {}).filter(([key]) =>
      getConfigFieldNodes(getSchemaProperties(configSchema)?.[key])?.includes(
        nodeId
      )
    )
  );
};

export const isSchemaMessageType = (schema: JSONSchema7Definition) => {
  if (typeof schema === 'boolean' || schema == null) return false;
  if (schema.$ref && messageTypes.has(schema.$ref)) return true;
  if (
    schema.type === 'object' &&
    schema.title &&
    messageTypeTitles.has(schema.title)
  ) {
    return true;
  }
  return false;
};

export const isSchemaMessageArray = (schema: JSONSchema7) => {
  if (typeof schema !== 'object' || schema == null) return false;

  // used for runtime generated schemas
  if ('langgraph_type' in schema && schema.langgraph_type === 'messages') {
    return true;
  }

  return (
    schema.type === 'array' &&
    schema.items !== undefined &&
    !Array.isArray(schema.items) &&
    typeof schema.items !== 'boolean' &&
    (isSchemaMessageType(schema.items) ||
      schema.items.anyOf?.some(isSchemaMessageType) ||
      schema.items.oneOf?.some(isSchemaMessageType))
  );
};

export type OutdatedSeverity = 'low' | 'medium' | 'critical';

export const isOutdatedLanggraphApi = ({
  // both of these are semver strings
  latestVersion,
  currentVersion,
}: {
  latestVersion: string | undefined;
  currentVersion: string | undefined;
}): {
  outdated: boolean;
  severity: OutdatedSeverity | null;
} => {
  if (!latestVersion) {
    return { outdated: false, severity: null };
  }

  // old versions of the server don't return a version
  if (!currentVersion) {
    return { outdated: true, severity: 'critical' };
  }

  if (semver.gte(currentVersion, latestVersion)) {
    return { outdated: false, severity: null };
  }

  const diffType = semver.diff(currentVersion, latestVersion);

  switch (diffType) {
    case 'major':
      return { outdated: true, severity: 'critical' };
    case 'minor':
      return { outdated: true, severity: 'medium' };
    case 'patch': {
      // If more than 3 patch versions behind, make it medium severity
      const currentPatch = semver.patch(currentVersion);
      const latestPatch = semver.patch(latestVersion);
      const patchDiff = latestPatch - currentPatch;

      if (patchDiff > 3) {
        return { outdated: true, severity: 'medium' };
      }
      return { outdated: true, severity: 'low' };
    }
    case 'prerelease':
      return { outdated: true, severity: 'low' };
    default:
      return { outdated: true, severity: 'medium' };
  }
};

export const getNodeNameFromRun = (run?: RunSchema): string | undefined => {
  const metadata = run?.extra?.metadata;
  if (!metadata) return;
  const langgraphNode = metadata?.langgraph_node;
  const nameSpace = metadata?.langgraph_checkpoint_ns;
  // convert the checkpoint namespace to the series of node names.
  // this is to account for subgraphs where node names should be of the structre node_a:node_b:node_c
  const nodesWithSubgraph = parseLangGraphCheckpointNs(nameSpace);
  const nodeName = nodesWithSubgraph.reduce((acc, curr) => {
    const { name } = curr;
    if (acc) {
      return acc + ':' + name;
    }
    return name;
  }, '');
  // fallback to the langgraph_node if the nodeName is not found
  return nodeName || langgraphNode;
};
